import type { Receipt } from "@sobidogam/storage";
import { useState } from "react";

export function ReceiptItemsEditor({
	receipt,
	knownItems,
	onUpdate,
}: {
	receipt: Receipt;
	knownItems: string[];
	onUpdate: (id: string, patch: Partial<Receipt>) => Promise<boolean>;
}) {
	const [names, setNames] = useState<string[]>([]);
	const [name, setName] = useState("");
	const [details, setDetails] = useState<
		Record<string, { qty: string; amount: string }>
	>({});
	const invalidDetails = names.some((item) => {
		const value = details[item];
		return (
			value &&
			((value.qty !== "" &&
				(!Number.isSafeInteger(Number(value.qty)) || Number(value.qty) <= 0)) ||
				(value.amount !== "" && !Number.isSafeInteger(Number(value.amount))))
		);
	});
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState("");
	const existing = (receipt.items ?? []).map((item) => item.name);
	const add = (value: string) => {
		const clean = value
			.normalize("NFKC")
			.trim()
			.replace(/\s+/g, " ")
			.slice(0, 80);
		if (clean && !existing.includes(clean) && !names.includes(clean))
			setNames([...names, clean]);
		setName("");
		setMessage("");
	};
	return (
		<details className="receipt-items-editor">
			<summary>
				{existing.length
					? `산 물건 ${existing.length}개 · 품목 더하기`
					: "무엇을 샀나요? 집 안 물건 연결"}
			</summary>
			<p className="field-hint">
				산 물건 이름만 남겨도 재고와 다음 장보기에 이어져요. 가격과 수량을 알고
				있다면 품목을 추가한 뒤 적어 주세요. 모르는 값은 비워 두면 돼요.
			</p>
			{existing.length > 0 && (
				<p className="existing-items">{existing.join(" · ")}</p>
			)}
			<div className="item-suggestions">
				{[...new Set(knownItems)]
					.filter((item) => !existing.includes(item))
					.slice(0, 8)
					.map((item) => (
						<button
							key={item}
							type="button"
							aria-pressed={names.includes(item)}
							onClick={() =>
								names.includes(item)
									? setNames(names.filter((name) => name !== item))
									: add(item)
							}
						>
							{item}
						</button>
					))}
			</div>
			<label>
				추가할 물건
				<input
					maxLength={80}
					value={name}
					placeholder="예: 세탁 세제"
					onChange={(event) => setName(event.target.value)}
				/>
			</label>
			<button
				type="button"
				className="quiet-button"
				disabled={!name.trim() || busy}
				onClick={() => add(name)}
			>
				품목 추가
			</button>
			<ul className="selected-items">
				{names.map((item) => (
					<li key={item}>
						{item}
						<div className="receipt-line-details">
							<label>
								{item} 수량 (선택)
								<input
									inputMode="numeric"
									value={details[item]?.qty ?? ""}
									onChange={(event) =>
										setDetails({
											...details,
											[item]: {
												amount: details[item]?.amount ?? "",
												qty: event.target.value.replace(/[^0-9]/g, ""),
											},
										})
									}
								/>
							</label>
							<label>
								{item} 품목 합계 (원, 선택)
								<input
									inputMode="numeric"
									value={details[item]?.amount ?? ""}
									onChange={(event) =>
										setDetails({
											...details,
											[item]: {
												qty: details[item]?.qty ?? "",
												amount: event.target.value.replace(/[^0-9]/g, ""),
											},
										})
									}
								/>
							</label>
						</div>
						<button
							type="button"
							aria-label={`${item} 제외`}
							onClick={() => setNames(names.filter((name) => name !== item))}
						>
							×
						</button>
					</li>
				))}
			</ul>
			<button
				type="button"
				className="button button-small"
				disabled={busy || invalidDetails || (!names.length && !name.trim())}
				aria-busy={busy}
				onClick={async () => {
					const added = [
						...new Set([
							...names,
							...(name.trim() ? [name.normalize("NFKC").trim()] : []),
						]),
					].filter((item) => !existing.includes(item));
					setBusy(true);
					setMessage("");
					try {
						const ok = await onUpdate(receipt.receiptId, {
							items: [
								...(receipt.items ?? []),
								...added.map((name) => ({
									name,
									amount: details[name]?.amount
										? Number(details[name]?.amount)
										: null,
									qty: details[name]?.qty ? Number(details[name]?.qty) : null,
								})),
							],
						});
						if (ok) {
							setNames([]);
							setDetails({});
							setName("");
							setMessage(
								"산 물건을 연결했어요. 장보기의 물건 탭에서 확인할 수 있어요.",
							);
						} else
							setMessage(
								"저장하지 못했어요. 입력을 유지했으니 다시 시도해 주세요.",
							);
					} catch {
						setMessage(
							"저장하지 못했어요. 입력을 유지했으니 다시 시도해 주세요.",
						);
					} finally {
						setBusy(false);
					}
				}}
			>
				{busy ? "연결 중…" : "산 물건 연결하기"}
			</button>
			{invalidDetails ? (
				<p className="field-hint">
					수량은 1 이상, 금액은 저장할 수 있는 범위의 정수로 적어 주세요.
				</p>
			) : null}
			{message && <output>{message}</output>}
		</details>
	);
}
